import React, { useEffect, useMemo, useState } from 'react'

import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import _ from 'lodash'
import { officesService } from 'ziphy-web-shared/basic/api'
import { isAbsoluteEmpty } from 'ziphy-web-shared/basic/helpers'
import { useLoader, useUpdateCallback } from 'ziphy-web-shared/basic/hooks'
import { Button } from 'ziphy-web-shared/basic/lib/buttons'
import { useForm } from 'ziphy-web-shared/basic/lib/forms'
import { LegacyInput } from 'ziphy-web-shared/basic/lib/forms/fields'
import { BubbleLoader } from 'ziphy-web-shared/basic/lib/loaders'
import { ProgressRing } from 'ziphy-web-shared/basic/lib/loaders'

import { $booking } from '@store'

import VoucherCard from '../voucherCard/VoucherCard'
import styles from './SelectCode.module.scss'

const SelectCode = ({ officeId = null, onChange }) => {
  const { t } = useTranslation()

  const availablePracticeIds = useMemo(() => {
    return $booking.hasVoucherOrCode
      ? [$booking.practiceId]
      : $booking.getFilteredPractices(false).map((x) => x.id)
  }, [$booking.hasVoucherOrCode, $booking.practiceId])

  const [loadedData, setLoadedData] = useState(null)
  const [submittedId, setSubmittedId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // Load init data
  const { isLoading: isInitLoading } = useLoader(async () => {
    if (officeId && !submittedId) {
      const response = await officesService.search({
        id: officeId,
        practiceIds: availablePracticeIds,
        zip: $booking.usablePlace.zip,
      })
      setLoadedData({
        offices: response.prepared?.offices.items || [],
        practices: response.prepared?.practices || [],
      })
      setSubmittedId(officeId)
    }
  }, [officeId, availablePracticeIds])

  // Search and select
  const { form, useFormOnChange } = useForm({ searchString: { value: '' } })
  const { searchString } = form.fields
  const [selectedId, setSelectedId] = useState(null)

  const updateCallback = useUpdateCallback(async () => {
    const code = _.trim(searchString.value)
    if (isAbsoluteEmpty(code)) {
      setIsLoading(false)
      return handleClear()
    }
    const response = await officesService.search({
      code,
      practiceIds: availablePracticeIds,
      zip: $booking.usablePlace.zip,
    })
    handleClear()
    setLoadedData({
      offices: response.prepared?.offices.items || [],
      practices: response.prepared?.practices || [],
    })
    setIsLoading(false)
  })

  useEffect(() => {
    if (searchString.value) {
      setIsLoading(true)
    }
  }, [searchString.value])

  useFormOnChange(updateCallback, [searchString.value])

  function handleClear() {
    setLoadedData(null)
    setSelectedId(null)
  }

  // clear after voucher changing
  useEffect(() => {
    handleClear()
    searchString.onChange('')
  }, [availablePracticeIds])

  // View
  const preparedList = useMemo(() => {
    const list = _.map(loadedData?.offices, (office) => {
      const practice = loadedData.practices.find((x) => x.id === office.practiceId)
      return { office, practice }
    })

    return _.orderBy(list, 'office.name')
  }, [loadedData?.offices])

  const preparedSubmittedItem = useMemo(() => {
    return preparedList.find((x) => x.office.id === submittedId)
  }, [submittedId])

  // Submit
  function handleChooseAnother() {
    searchString.onChange(preparedSubmittedItem.office.code)
    setSubmittedId(null)
    onChange({ officeId: null })
  }

  function handleSubmit() {
    setSubmittedId(selectedId)
    onChange({ officeId: selectedId })
    setSelectedId(null)
  }

  return (
    <div className="mt-30">
      <div className="title-sm">
        {t('label.code')}
        {preparedSubmittedItem && (
          <span className="text-muted"> {preparedSubmittedItem.office.code}</span>
        )}
      </div>

      {isInitLoading ? (
        <div className="box-body box-body--padding text-center">
          <ProgressRing progress={25} spin={true} />
        </div>
      ) : (
        <>
          {preparedSubmittedItem ? (
            <VoucherCard
              title={preparedSubmittedItem.practice.name}
              subTitle={preparedSubmittedItem.office.name}
              buttonLabel={t('book.vouchers.btn.choose_another_code')}
              onButtonClick={handleChooseAnother}
            />
          ) : (
            <>
              <div className="mb-10">{t('book.vouchers.code_subtitle')}</div>
              <LegacyInput
                field={searchString}
                label={t('label.practice_code')}
                placeholder={t('label.enter_the_practice_code_here')}
                mode="lg"
                clear={true}
                onClear={handleClear}
              />
              {isLoading ? (
                <div className={styles.loader}>
                  <BubbleLoader />
                </div>
              ) : !preparedList.length && searchString.value ? (
                <div className={styles.notFound}>{t('ps.errors.no_results')}</div>
              ) : (
                preparedList.map((item) => (
                  <VoucherCard
                    key={item.office.id}
                    className="mt-10"
                    title={item.practice.name}
                    subTitle={item.office.name}
                    isCheckbox={true}
                    isChecked={selectedId === item.office.id}
                    onClick={() => setSelectedId(item.office.id)}
                  />
                ))
              )}
              {selectedId && (
                <Button mode={['primary', 'block']} className="mt-10" onClick={handleSubmit}>
                  {t('btn.submit')}
                </Button>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default observer(SelectCode)
